/* eslint-disable */
// ─── YogaStay SearchBar — BookRetreats-inspired ───────────────────────────────
// Features:
//   • Month-strip picker (not date-range — better for retreat planning)
//   • 1-minute quiz flow for users who don't know what they want
//   • "X people interested" counter on retreat cards (exported hook)
//   • Concierge CTA for high-intent undecided users
//   • Hero variant (dark bg) + Page variant (light bg)
//   • Fully mobile-first, no portals needed

import { useState, useRef, useEffect, useCallback } from "react";
import { StyleDot } from "./styleTokens";
import { Search, Flower2, Leaf, Activity, Brain, GraduationCap, CalendarDays, Zap, Moon, CalendarRange, Infinity, Shuffle, MapPin, Mountain, Palmtree, Sailboat, Landmark, Plane, Wallet, Sprout, Star, Gem, Sparkles, Flame } from "lucide-react";
import { createPortal } from "react-dom";
import { track, EVENTS } from "./analytics";
import { extraQuizRegions } from "./content/ytt";

// ─── EVENT TRACKER ───────────────────────────────────────────────────────────
// Fires anonymous events to search_events table. No PII unless user submits
// concierge form with their email.
import { SUPABASE_URL, SUPABASE_ANON_KEY } from './security/supabaseClient';

function getSessionId() {
  let sid = localStorage.getItem("ys_sid");
  if (!sid) { sid = Math.random().toString(36).slice(2); localStorage.setItem("ys_sid", sid); }
  return sid;
}

function getUserToken() {
  try {
    const u = localStorage.getItem("yogastay_user");
    if (u) { const p = JSON.parse(u); if (p.token) return p.token; }
  } catch {}
  return SUPABASE_ANON_KEY;
}

async function trackEvent(payload) {
  try {
    const token = getUserToken();
    await fetch(`${SUPABASE_URL}/rest/v1/search_events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
        Prefer: "return=minimal",
      },
      body: JSON.stringify({ ...payload, session_id: getSessionId() }),
    });
  } catch { /* non-blocking — never crash the UI */ }
}



// ─── Month strip data ────────────────────────────────────────────────────────
function getMonths(count = 12) {
  const months = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`,
      label: d.toLocaleDateString("en-IN", { month: "short" }),
      year:  d.getFullYear(),
      month: d.getMonth(),
      full:  d.toLocaleDateString("en-IN", { month: "long", year: "numeric" }),
    });
  }
  return months;
}

// ─── CSS ─────────────────────────────────────────────────────────────────────
const css = `
  .ys-sb-wrap { width:100%; max-width:820px; margin:0 auto; position:relative; z-index:200; isolation:isolate; }

  /* ── Pill bar ── */
  .ys-sb {
    display:flex; align-items:stretch; background:white;
    border-radius:60px; box-shadow:0 4px 24px rgba(0,0,0,0.12);
    padding:5px; gap:0; position:relative;
    border:1px solid rgba(255,255,255,0.2);
    z-index:200;
  }
  .ys-sb.page {
    box-shadow:0 2px 14px rgba(181,112,63,0.15);
    border:1px solid rgba(181,112,63,0.2);
  }

  /* ── Each pill field ── */
  .ys-pill {
    flex:1; min-width:0; padding:10px 18px; border:none; background:none;
    cursor:pointer; text-align:left; font-family:'Jost',sans-serif;
    border-radius:50px; transition:background 0.13s; position:relative;
  }
  .ys-pill:hover { background:rgba(181,112,63,0.07); }
  .ys-pill.active { background:rgba(181,112,63,0.1); }
  .ys-pill-divider {
    width:1px; background:rgba(181,112,63,0.2);
    margin:10px 0; flex-shrink:0;
  }
  .ys-pill-label {
    display:block; font-size:10px; font-weight:700;
    letter-spacing:1.5px; text-transform:uppercase;
    color:#8a8a9a; margin-bottom:3px;
  }
  .ys-pill-value {
    display:block; font-size:14px; color:#2E2A24; white-space:nowrap;
    overflow:hidden; text-overflow:ellipsis;
  }
  .ys-pill-value.empty { color:#aaa; }

  /* ── Search button ── */
  .ys-search-btn {
    background:linear-gradient(135deg,#985A30,#B5703F);
    border:none; border-radius:50px; cursor:pointer;
    padding:0 22px; display:flex; align-items:center; justify-content:center;
    gap:7px; font-family:'Jost',sans-serif; font-size:12px;
    font-weight:700; letter-spacing:1.5px; text-transform:uppercase;
    color:white; white-space:nowrap; flex-shrink:0;
    transition:all 0.15s; min-height:48px; min-width:110px;
    box-shadow:0 4px 14px rgba(152,90,48,0.4);
  }
  .ys-search-btn:hover { transform:scale(1.03); box-shadow:0 6px 20px rgba(152,90,48,0.5); }

  /* ── Dropdown ── */
  .ys-dropdown {
    position:absolute; top:calc(100% + 10px); left:0;
    background:white; border-radius:20px; padding:16px;
    box-shadow:0 12px 48px rgba(0,0,0,0.16);
    border:1px solid rgba(181,112,63,0.15);
    z-index:9000; min-width:320px; animation:ysFadeIn 0.15s ease;
  }
  .ys-dropdown.right { left:auto; right:0; }
  @keyframes ysFadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }

  /* ── Month strip ── */
  .ys-months {
    display:flex; gap:6px; overflow-x:auto; padding:4px 0 8px;
    scrollbar-width:none; -webkit-overflow-scrolling:touch;
  }
  .ys-months::-webkit-scrollbar { display:none; }
  .ys-month-btn {
    display:flex; flex-direction:column; align-items:center;
    padding:9px 14px; border-radius:12px; border:1.5px solid rgba(181,112,63,0.2);
    background:white; cursor:pointer; font-family:'Jost',sans-serif;
    flex-shrink:0; transition:all 0.13s; min-width:58px;
  }
  .ys-month-btn:hover { border-color:#B5703F; background:rgba(181,112,63,0.06); }
  .ys-month-btn.selected {
    border-color:#B5703F; background:rgba(181,112,63,0.12);
    color:#A05F33; font-weight:600;
  }
  .ys-month-name { font-size:13px; font-weight:500; color:#2E2A24; }
  .ys-month-year { font-size:9px; color:#8a8a9a; margin-top:1px; letter-spacing:0.5px; }
  .ys-month-btn.selected .ys-month-name { color:#A05F33; }

  /* ── Style chips ── */
  .ys-style-grid {
    display:grid; grid-template-columns:repeat(auto-fill,minmax(120px,1fr)); gap:7px;
  }
  .ys-style-chip {
    display:flex; align-items:center; gap:7px; padding:8px 11px;
    border:1.5px solid rgba(181,112,63,0.2); border-radius:10px;
    cursor:pointer; font-family:'Jost',sans-serif; font-size:12px;
    background:white; transition:all 0.12s; text-align:left;
  }
  .ys-style-chip:hover { border-color:#B5703F; background:rgba(181,112,63,0.06); }
  .ys-style-chip.selected { border-color:#B5703F; background:rgba(181,112,63,0.12); color:#A05F33; font-weight:600; }

  /* ── Mobile ── */
  @media (max-width:700px) {
    .ys-sb { border-radius:16px; flex-direction:column; padding:8px; gap:4px; }
    .ys-pill-divider { display:none; }
    .ys-pill { border-radius:12px; padding:10px 14px; }
    .ys-search-btn { border-radius:12px; min-height:50px; width:100%; }
    .ys-dropdown { left:0; right:0; min-width:unset; }
    .ys-pill-label { font-size:9px; }
    .ys-pill-value { font-size:13px; }
  }

  /* ── Quiz overlay ── */
  .ys-quiz-overlay {
    position:fixed; inset:0; background:rgba(0,0,0,0.55);
    z-index:9999; display:flex; align-items:center; justify-content:center;
    padding:16px; backdrop-filter:blur(4px); animation:ysFadeIn 0.2s ease;
  }
  .ys-quiz-card {
    background:white; border-radius:24px; width:100%; max-width:480px;
    padding:28px 26px; position:relative;
    box-shadow:0 24px 80px rgba(0,0,0,0.22);
    animation:ysFadeIn 0.2s ease;
  }
  .ys-quiz-progress {
    display:flex; gap:5px; margin-bottom:24px;
  }
  .ys-quiz-bar {
    flex:1; height:3px; border-radius:2px; background:rgba(181,112,63,0.15);
    transition:background 0.3s;
  }
  .ys-quiz-bar.done { background:#B5703F; }
  .ys-quiz-option {
    width:100%; padding:13px 16px; border:1.5px solid rgba(181,112,63,0.25);
    border-radius:12px; cursor:pointer; font-family:'Jost',sans-serif;
    font-size:14px; text-align:left; background:white; display:flex;
    align-items:center; gap:12px; margin-bottom:8px;
    transition:all 0.13s; color:#2E2A24;
  }
  .ys-quiz-option:hover { border-color:#B5703F; background:rgba(181,112,63,0.07); }
  .ys-quiz-option.selected { border-color:#B5703F; background:rgba(181,112,63,0.12); font-weight:600; color:#A05F33; }

  /* ── Type toggle (All / Retreats / Teacher Training / Classes) ── */
  .ys-scope { display:flex; justify-content:center; gap:6px; flex-wrap:wrap; margin-bottom:12px; }
  .ys-scope-btn { padding:6px 14px; border-radius:20px; border:1px solid rgba(255,255,255,0.4); background:rgba(255,255,255,0.12);
    color:rgba(255,255,255,0.92); font-family:'Jost',sans-serif; font-size:12.5px; cursor:pointer; min-height:34px; }
  .ys-scope-btn.on { background:white; color:#2E2A24; border-color:white; font-weight:600; }
  .ys-scope.page .ys-scope-btn { border-color:rgba(181,112,63,0.3); background:white; color:#4a4a5a; }
  .ys-scope.page .ys-scope-btn.on { background:#2E2A24; color:white; border-color:#2E2A24; }

  /* ── Popular pills ── */
  .ys-popular { display:flex; gap:7px; flex-wrap:wrap; margin-top:14px; justify-content:center; }
  .ys-popular-pill {
    padding:5px 13px; border-radius:20px; border:1px solid rgba(255,255,255,0.3);
    background:rgba(255,255,255,0.13); color:rgba(255,255,255,0.88);
    cursor:pointer; font-family:'Jost',sans-serif; font-size:12px;
    white-space:nowrap; transition:all 0.13s; min-height:32px;
    display:flex; align-items:center;
  }
  .ys-popular-pill:hover { background:rgba(255,255,255,0.22); color:white; }
  .ys-popular-pill.page {
    border-color:rgba(181,112,63,0.3); background:rgba(181,112,63,0.07);
    color:#A05F33;
  }
  .ys-popular-pill.page:hover { background:rgba(181,112,63,0.14); }

  /* ── Concierge CTA ── */
  .ys-concierge {
    margin-top:18px; padding:14px 18px;
    background:rgba(181,112,63,0.07);
    border:1px dashed rgba(181,112,63,0.35);
    border-radius:14px; display:flex; align-items:center;
    gap:12px; cursor:pointer; transition:all 0.15s;
    font-family:'Jost',sans-serif;
  }
  .ys-concierge:hover { background:rgba(181,112,63,0.13); border-color:#B5703F; }
`;

// ─── QUIZ DATA ────────────────────────────────────────────────────────────────
const QUIZ_STEPS = [
  {
    q: "What are you looking for?",
    Icon: Search,
    key: "goal",
    options: [
      { Icon:Flower2,     label:"Deepen my yoga practice" },
      { Icon:Leaf,        label:"Relax & recharge" },
      { Icon:Activity,    label:"Transform my body" },
      { Icon:Brain,       label:"Mental clarity & meditation" },
      { Icon:GraduationCap, label:"Become a yoga teacher" },
    ],
  },
  {
    q: "How long can you go for?",
    Icon: CalendarDays,
    key: "duration",
    options: [
      { Icon:Zap,         label:"A weekend (2–3 days)" },
      { Icon:Moon,        label:"A week (5–7 days)" },
      { Icon:CalendarRange, label:"Two weeks" },
      { Icon:Infinity,    label:"A month or more" },
      { Icon:Shuffle,     label:"I'm flexible" },
    ],
  },
  {
    q: "Where do you want to go?",
    Icon: MapPin,
    key: "location",
    options: [
      { Icon:Mountain,    label:"India (Rishikesh, Goa, Kerala)" },
      { Icon:Palmtree,    label:"Bali, Indonesia" },
      { Icon:Sailboat,    label:"Thailand" },
      { Icon:Landmark,    label:"Europe" },
      { Icon:Plane,       label:"Anywhere — surprise me!" },
    ],
  },
  {
    q: "What's your budget per person?",
    Icon: Wallet,
    key: "budget",
    options: [
      { Icon:Sprout,      label:"Under $500" },
      { Icon:Leaf,        label:"$500 – $1,500" },
      { Icon:Star,        label:"$1,500 – $3,000" },
      { Icon:Gem,         label:"$3,000+" },
      { Icon:Shuffle,     label:"Flexible / unsure" },
    ],
  },
];

// ─── QUIZ COMPONENT ───────────────────────────────────────────────────────────
function QuizModal({ onClose, onComplete, ytts = [] }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  // "Become a yoga teacher" adds a certification question, and the location
  // step offers every country that has a Teacher Training but no answer yet.
  const teacher = !!answers.goal?.includes("yoga teacher");
  const steps = teacher ? (() => {
    const [goal, ...rest] = QUIZ_STEPS;
    const cert = { q: "Which certification?", Icon: GraduationCap, key: "certification", options: [
      { Icon: Sprout, label: "100-hour" }, { Icon: Leaf, label: "200-hour" }, { Icon: Star, label: "300-hour" },
      { Icon: Gem, label: "500-hour" }, { Icon: Shuffle, label: "Not sure yet" } ] };
    const extra = extraQuizRegions(ytts).map(c => ({ Icon: MapPin, label: c }));
    const withRegions = rest.map(st => st.key !== "location" ? st : {
      ...st, options: [...st.options.slice(0, -1), ...extra, st.options[st.options.length - 1]],
    });
    return [goal, cert, ...withRegions];
  })() : QUIZ_STEPS;
  const total = steps.length;

  const handleSelect = (option) => {
    setSelected(option.label);
    setTimeout(() => {
      const newAnswers = { ...answers, [steps[step].key]: option.label };
      setAnswers(newAnswers);
      if (step < total - 1) {
        setStep(s => s + 1);
        setSelected(null);
      } else {
        // Track quiz completion
        trackEvent({
          event_type: "quiz_complete",
          quiz_goal:     newAnswers.goal,
          quiz_duration: newAnswers.duration,
          quiz_location: newAnswers.location,
          quiz_budget:   newAnswers.budget,
        });
        track(EVENTS.QUIZ_COMPLETED, { goal: newAnswers.goal, duration: newAnswers.duration, location: newAnswers.location, budget: newAnswers.budget });
        onComplete(newAnswers);
      }
    }, 280);
  };

  const cur = steps[step];

  return (
    <div className="ys-quiz-overlay" onClick={e => e.target===e.currentTarget&&onClose()}>
      <div className="ys-quiz-card">
        {/* Progress */}
        <div className="ys-quiz-progress">
          {Array.from({length:total}).map((_,i)=>(
            <div key={i} className={`ys-quiz-bar${i<=step?" done":""}`} />
          ))}
        </div>

        {/* Close */}
        <button onClick={onClose} style={{ position:"absolute", top:16, right:18, background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#8a8a9a", lineHeight:1 }}>✕</button>

        {/* Step counter */}
        <div style={{ fontSize:11, color:"#B5703F", letterSpacing:2, textTransform:"uppercase", fontFamily:"'Jost',sans-serif", marginBottom:8 }}>
          Step {step+1} of {total}
        </div>

        {/* Question */}
        <div style={{ marginBottom:6, display:"flex", justifyContent:"center", color:"#985A30" }}><cur.Icon size={21} /></div>
        <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:400, color:"#2E2A24", marginBottom:18, lineHeight:1.3 }}>
          {cur.q}
        </h3>

        {/* Options */}
        {cur.options.map(opt => (
          <button key={opt.label} className={`ys-quiz-option${selected===opt.label?" selected":""}`}
            onClick={()=>handleSelect(opt)}>
            <opt.Icon size={18} color="#985A30" style={{ flexShrink:0 }} />
            <span>{opt.label}</span>
            {selected===opt.label && <span style={{ marginLeft:"auto", color:"#B5703F" }}>✓</span>}
          </button>
        ))}

        {/* Back */}
        {step > 0 && (
          <button onClick={()=>{setStep(s=>s-1);setSelected(null);}}
            style={{ background:"none", border:"none", cursor:"pointer", color:"#8a8a9a", fontFamily:"'Jost',sans-serif", fontSize:12, marginTop:8, padding:0 }}>
            ← Back
          </button>
        )}
      </div>
    </div>
  );
}

// ─── CONCIERGE MODAL ──────────────────────────────────────────────────────────
function ConciergeModal({ onClose, quizAnswers = null }) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    if (!email.trim()) return;
    track(EVENTS.CONCIERGE_SUBMITTED, { from_quiz: !!quizAnswers });
    setSent(true); // optimistic — show success immediately
    try {
      const token = getUserToken();
      // Save to concierge_leads table (has email + note columns)
      await fetch(`${SUPABASE_URL}/rest/v1/concierge_leads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${token}`,
          Prefer: "return=minimal",
        },
        body: JSON.stringify({
          email:       email.trim(),
          note:        note.trim() || null,
          quiz_goal:      quizAnswers?.goal     || null,
          quiz_duration:  quizAnswers?.duration || null,
          quiz_location:  quizAnswers?.location || null,
          quiz_budget:    quizAnswers?.budget   || null,
          session_id:  getSessionId(),
          created_at:  new Date().toISOString(),
        }),
      });
      // Also track as event for analytics
      trackEvent({ event_type: "concierge", destination: quizAnswers?.location || null });
    } catch { /* non-blocking */ }
  };

  return (
    <div className="ys-quiz-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="ys-quiz-card">
        <button onClick={onClose} style={{ position:"absolute", top:16, right:18, background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#8a8a9a" }}>✕</button>
        {sent ? (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ marginBottom:14, display:"flex", justifyContent:"center" }}><Sparkles size={42} color="#B5703F" /></div>
            <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:400, color:"#2E2A24", marginBottom:8 }}>We'll be in touch!</h3>
            <p style={{ color:"#8a8a9a", fontSize:14 }}>Our team will hand-pick retreat options and email you within 24 hours.</p>
            <button onClick={onClose} style={{ marginTop:20, padding:"10px 24px", background:"#B5703F", border:"none", borderRadius:8, cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, color:"white", letterSpacing:1.5, textTransform:"uppercase", fontWeight:600 }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ marginBottom:8, display:"flex", justifyContent:"center" }}><Flower2 size={26} color="#B5703F" /></div>
            <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:400, color:"#2E2A24", marginBottom:6 }}>Let us hand-pick for you</h3>
            <p style={{ color:"#8a8a9a", fontSize:13, marginBottom:20, lineHeight:1.6 }}>
              Tell us what you're looking for. Our team of yogis will curate the perfect retreat options and get back to you within 24 hours — completely free.
            </p>
            <div style={{ marginBottom:12 }}>
              <label style={{ display:"block", fontSize:11, letterSpacing:1.5, textTransform:"uppercase", color:"#8a8a9a", marginBottom:6, fontFamily:"'Jost',sans-serif", fontWeight:600 }}>Your email *</label>
              <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com"
                style={{ width:"100%", padding:"11px 14px", border:"1.5px solid rgba(181,112,63,0.3)", borderRadius:10, fontFamily:"'Jost',sans-serif", fontSize:14, outline:"none", color:"#2E2A24" }} />
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ display:"block", fontSize:11, letterSpacing:1.5, textTransform:"uppercase", color:"#8a8a9a", marginBottom:6, fontFamily:"'Jost',sans-serif", fontWeight:600 }}>What are you looking for?</label>
              <textarea value={note} onChange={e=>setNote(e.target.value)} placeholder="E.g. 7-day Vinyasa retreat in Bali, budget $800, solo traveller..."
                rows={3} style={{ width:"100%", padding:"11px 14px", border:"1.5px solid rgba(181,112,63,0.3)", borderRadius:10, fontFamily:"'Jost',sans-serif", fontSize:14, outline:"none", color:"#2E2A24", resize:"none" }} />
            </div>
            <button onClick={handleSend} disabled={!email.trim()} style={{ width:"100%", padding:"13px", background:email.trim()?"#B5703F":"rgba(181,112,63,0.3)", border:"none", borderRadius:10, cursor:email.trim()?"pointer":"default", fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:1.5, textTransform:"uppercase", color:"white", fontWeight:700, transition:"all 0.15s" }}>
              Send Request →
            </button>
            <p style={{ textAlign:"center", fontSize:11, color:"#8a8a9a", marginTop:10 }}>Free · No commitment · Reply within 24 hrs</p>
          </>
        )}
      </div>
    </div>
  );
}

// ─── PORTAL DROPDOWN (module-level so it never remounts on parent re-render) ──
function PortalDropdown({ triggerRef, align="left", width, children, onMount, onUnmount }) {
  const [pos, setPos] = useState(null);
  // When the panel would run past the bottom of the screen (a phone, or a pill
  // low on the page), it opens as a bottom sheet instead: fixed above the
  // bottom tab bar and scrollable, so nothing in it is ever out of reach.
  // Decided from the room left below the pill, not by measuring the panel —
  // measuring caught it mid-animation with fonts still loading, too short to
  // trip the check. Desktop keeps the anchored dropdown.
  const [sheet, setSheet] = useState(false);
  const nodeRef = useRef(null);
  useEffect(() => {
    if (!triggerRef?.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    if (window.innerWidth < 700 && window.innerHeight - rect.bottom < 360) setSheet(true);
    setPos({
      top:  rect.bottom + window.scrollY + 8,
      left: align==="right"
        ? rect.right + window.scrollX - (typeof width==="number"?width:320)
        : rect.left  + window.scrollX,
    });
  }, []);
  useEffect(() => {
    const node = nodeRef.current;
    if (node) { onMount?.(node); return () => onUnmount?.(node); }
  }, [pos]);
  if (!pos) return null;
  return createPortal(
    <div ref={nodeRef} style={sheet ? {
      position:"fixed", left:12, right:12, bottom:76, maxHeight:"calc(100vh - 150px)", overflowY:"auto", zIndex:99999, borderRadius:20,
    } : {
      position:"absolute", top:pos.top, left:pos.left, zIndex:99999,
      width: typeof width==="number" ? width : "auto",
    }}>
      <div style={{
      background:"white", borderRadius:20, padding:16,
      boxShadow:"0 12px 48px rgba(0,0,0,0.16)",
      border:"1px solid rgba(181,112,63,0.15)",
      animation:"ysFadeIn 0.15s ease",
      fontFamily:"'Jost',sans-serif",
    }}>
      {children}
    </div>
    </div>,
    document.body
  );
}

// ─── MAIN SEARCH BAR ──────────────────────────────────────────────────────────
export default function SearchBar({
  search, setSearch,
  selectedMonth, setSelectedMonth,   // "YYYY-MM" string e.g. "2026-03"
  filterStyle, setFilterStyle,
  onSearch,
  // Destinations derived from live inventory: [{ name, count }]. Falls back to
  // the static list below when empty, so a failed load never blanks the menu.
  destinations = [],
  variant = "hero",                  // "hero" | "page"
  showConcierge = true,
  showQuiz = true,
  showPopular = true,
  // One search bar everywhere (Phase B). `scope` says what a search looks for;
  // `scopeOptions` ([[id, label]]) shows a small type toggle above the bar.
  scope = null, onScopeChange = null, scopeOptions = null,
  // Teacher Training programmes, for the quiz's certification branch, and what
  // to do when a quiz ends with "Become a yoga teacher".
  ytts = [], onTeacherTrainingQuiz = null,
  // Extra filters shown as a fourth pill (Yoga Teacher Training page):
  // { value: { priceMin, priceMax, language, accommodation, yaOnly }, onChange, languages, currency }
  extraFilters = null,
}) {
  const MONTHS = getMonths(14);
  const STYLES = ["All", "Hatha", "Vinyasa", "Yin", "Kundalini", "Meditation", "Ashtanga", "Restorative", "Retreat"];
  // Shown only until real inventory exists — these advertise places we may have
  // nothing in, so live destinations win whenever there are any.
  const POPULAR_FALLBACK = ["Rishikesh", "Bali", "Kerala", "Goa", "Mysore", "Thailand"];
  const POPULAR = destinations.length
    ? destinations.slice(0, 6).map(d => d.name)
    : POPULAR_FALLBACK;

  const [open, setOpen] = useState(null); // "where"|"when"|"style"
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [showConciergeModal, setShowConciergeModal] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const wrapRef   = useRef(null);
  const whereRef  = useRef(null);
  const whenRef   = useRef(null);
  const filtersRef = useRef(null);
  const styleRef  = useRef(null);
  const monthsRef = useRef(null);

  // Track portal containers so outside-click handler knows to ignore them
  const portalNodesRef = useRef(new Set());
  const addPortalNode    = useCallback(n => portalNodesRef.current.add(n),    []);
  const removePortalNode = useCallback(n => portalNodesRef.current.delete(n), []);

  // Close dropdown on outside click — but NOT when clicking inside a portal
  useEffect(() => {
    const handler = e => {
      const insideWrap   = wrapRef.current?.contains(e.target);
      const insidePortal = [...portalNodesRef.current].some(n => n.contains(e.target));
      if (!insideWrap && !insidePortal) setOpen(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Scroll to selected month in strip
  useEffect(() => {
    if (open === "when" && selectedMonth && monthsRef.current) {
      const idx = MONTHS.findIndex(m => m.key === selectedMonth);
      if (idx >= 0) {
        const btn = monthsRef.current.children[idx];
        btn?.scrollIntoView({ block:"nearest", inline:"center", behavior:"smooth" });
      }
    }
  }, [open, selectedMonth]);

  const fmtMonth = key => {
    if (!key) return null;
    const m = MONTHS.find(x => x.key === key);
    return m ? `${m.label} ${m.year}` : null;
  };

  // Every way of running a search goes through here, so search_submitted is
  // counted once per search. The quiz's own jump to browse is not a search.
  // Pass the destination when it was set in the same handler: `search` is
  // still the previous value until React re-renders.
  const submitSearch = (destination = search) => {
    track(EVENTS.SEARCH_SUBMITTED, { destination: destination || null, style: filterStyle || null, month: selectedMonth || null });
    onSearch?.();
  };
  // The quiz's Teacher Training branch offers every country that has a YTT. A
  // page that already has them passes `ytts`; otherwise they are fetched once
  // (country only, public data) when the quiz opens.
  const [quizYtts, setQuizYtts] = useState(ytts);
  useEffect(() => { if (ytts.length) setQuizYtts(ytts); }, [ytts.length]);
  const openQuiz = () => {
    track(EVENTS.QUIZ_STARTED);
    setShowQuizModal(true);
    if (!quizYtts.length) {
      fetch(`${SUPABASE_URL}/rest/v1/retreats?retreat_type=eq.teacher_training&is_active=eq.true&select=country,retreat_type&limit=500`,
        { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` } })
        .then(r => r.ok ? r.json() : []).then(d => Array.isArray(d) && setQuizYtts(d)).catch(() => {});
    }
  };

  const handleQuizComplete = (answers) => {
    setShowQuizModal(false);
    // "Become a yoga teacher" returns Teacher Trainings only (Phase B).
    if (answers.goal?.includes("yoga teacher") && onTeacherTrainingQuiz) {
      setQuizResult(answers);
      onTeacherTrainingQuiz(answers);
      return;
    }
    setQuizResult(answers);
    // Map quiz answers to search fields
    if (answers.location && answers.location !== "Anywhere — surprise me!") {
      const loc = answers.location.split("(")[0].trim();
      if (answers.location.includes("India")) setSearch("India");
      else if (answers.location.includes("Bali")) setSearch("Bali");
      else if (answers.location.includes("Thailand")) setSearch("Thailand");
    }
    if (answers.goal?.includes("yoga teacher")) setFilterStyle("Ashtanga");
    else if (answers.goal?.includes("meditation")) setFilterStyle("Meditation");
    else if (answers.goal?.includes("Relax")) setFilterStyle("Yin");
    onSearch?.();
  };

  const isHero = variant === "hero";

  return (
    <>
      <style>{css}</style>

      <div className="ys-sb-wrap" ref={wrapRef}>
        {scopeOptions && onScopeChange && (
          <div role="tablist" aria-label="What are you looking for?" className={`ys-scope${isHero ? "" : " page"}`}>
            {scopeOptions.map(([id, label]) => (
              <button key={id} role="tab" type="button" aria-selected={scope === id}
                className={`ys-scope-btn${scope === id ? " on" : ""}`} onClick={() => onScopeChange(id)}>{label}</button>
            ))}
          </div>
        )}
        {/* ── Main pill bar ── */}
        <div className={`ys-sb${isHero ? "" : " page"}`}>

          {/* WHERE */}
          <button ref={whereRef} className={`ys-pill${open==="where"?" active":""}`}
            onClick={()=>setOpen(o=>o==="where"?null:"where")}>
            <span className="ys-pill-label">Where</span>
            <span className={`ys-pill-value${search?" filled":" empty"}`}>
              {search || "Destination"}
            </span>
          </button>
          {open==="where" && (
            <PortalDropdown triggerRef={whereRef} width={280} onMount={addPortalNode} onUnmount={removePortalNode}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <div style={{ fontSize:11, letterSpacing:1.5, textTransform:"uppercase", color:"#8a8a9a", fontWeight:600 }}>
                  {destinations.length ? "Destinations" : "Popular destinations"}
                </div>
                {search && (
                  <button onMouseDown={e=>{e.stopPropagation();setSearch("");}}
                    style={{ background:"none", border:"none", cursor:"pointer", color:"#B5703F", fontFamily:"'Jost',sans-serif", fontSize:12 }}>Clear</button>
                )}
              </div>
              {(destinations.length
                ? destinations.map(d => ({ label: d.name, count: d.count }))
                : ["Ubud, Bali","Canggu, Bali","Chiang Mai, Thailand","Koh Samui, Thailand","Rishikesh, India","Kathmandu, Nepal","Tulum, Mexico"].map(n => ({ label: n, count: null }))
              ).map(dest=>(
                <button key={dest.label} onMouseDown={()=>{setSearch(dest.label);setOpen(null);submitSearch(dest.label);}}
                  style={{ display:"flex", alignItems:"center", gap:10, width:"100%", padding:"9px 10px", border:"none", background:"none", cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:13, color:"#2E2A24", textAlign:"left", borderRadius:8, transition:"background 0.1s" }}
                  onMouseEnter={e=>e.currentTarget.style.background="rgba(181,112,63,0.07)"}
                  onMouseLeave={e=>e.currentTarget.style.background="none"}>
                  <MapPin size={15} color="#985A30" />
                  <span style={{ flex:1 }}>{dest.label}</span>
                  {dest.count != null && (
                    <span style={{ fontSize:11, color:"#8a8a9a" }}>{dest.count}</span>
                  )}
                </button>
              ))}
              <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid rgba(181,112,63,0.1)" }}>
                <input value={search} onChange={e=>setSearch(e.target.value)}
                  placeholder="Type any destination…"
                  style={{ width:"100%", padding:"9px 12px", border:"1.5px solid rgba(181,112,63,0.25)", borderRadius:10, fontFamily:"'Jost',sans-serif", fontSize:13, outline:"none", color:"#2E2A24" }}
                  onClick={e=>e.stopPropagation()}
                  onKeyDown={e=>{ if(e.key==="Enter"){ setOpen(null); submitSearch(); } }} />
                {search.trim() && <p style={{ fontSize:11, color:"#B5703F", marginTop:6, paddingLeft:2 }}>Press Enter or click Search to find "{search}"</p>}
              </div>
              {/* Matches the "I'm flexible" reset in the When menu — there was no
                  way to undo a destination once picked except deleting the text. */}
              <button onMouseDown={e=>{e.stopPropagation();setSearch("");setOpen(null);submitSearch("");}}
                style={{ marginTop:8, width:"100%", padding:"8px", background:"none", border:"1.5px dashed rgba(181,112,63,0.3)", borderRadius:10, cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, color:"#8a8a9a" }}>
                Anywhere — show all destinations
              </button>
            </PortalDropdown>
          )}

          <div className="ys-pill-divider" />

          {/* WHEN — Month strip */}
          <button ref={whenRef} className={`ys-pill${open==="when"?" active":""}`}
            onClick={()=>setOpen(o=>o==="when"?null:"when")} style={{ flex:"0 0 auto", minWidth:130 }}>
            <span className="ys-pill-label">When</span>
            <span className={`ys-pill-value${selectedMonth?" filled":" empty"}`}>
              {fmtMonth(selectedMonth) || "Any month"}
            </span>
          </button>
          {open==="when" && (
            <PortalDropdown triggerRef={whenRef} width={340} onMount={addPortalNode} onUnmount={removePortalNode}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <div style={{ fontSize:11, letterSpacing:1.5, textTransform:"uppercase", color:"#8a8a9a", fontWeight:600 }}>Select month</div>
                {selectedMonth && (
                  <button onMouseDown={e=>{e.stopPropagation();setSelectedMonth("");}}
                    style={{ background:"none", border:"none", cursor:"pointer", color:"#B5703F", fontFamily:"'Jost',sans-serif", fontSize:12 }}>Clear</button>
                )}
              </div>
              <div className="ys-months" ref={monthsRef}>
                {MONTHS.map(m=>(
                  <button key={m.key} className={`ys-month-btn${selectedMonth===m.key?" selected":""}`}
                    onMouseDown={e=>{e.stopPropagation();setSelectedMonth(m.key);setOpen(null);}}>
                    <span className="ys-month-name">{m.label}</span>
                    <span className="ys-month-year">{m.year}</span>
                  </button>
                ))}
              </div>
              <button onMouseDown={e=>{e.stopPropagation();setSelectedMonth("");setOpen(null);}}
                style={{ marginTop:6, width:"100%", padding:"8px", background:"none", border:"1.5px dashed rgba(181,112,63,0.3)", borderRadius:10, cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, color:"#8a8a9a" }}>
                I'm flexible — show all dates
              </button>
            </PortalDropdown>
          )}

          <div className="ys-pill-divider" />

          {/* STYLE */}
          <button ref={styleRef} className={`ys-pill${open==="style"?" active":""}`}
            onClick={()=>setOpen(o=>o==="style"?null:"style")}>
            <span className="ys-pill-label">Style</span>
            <span className={`ys-pill-value${filterStyle&&filterStyle!=="All"?" filled":" empty"}`}>
              {filterStyle && filterStyle !== "All"
                ? <><StyleDot style={filterStyle} size={7} />{filterStyle}</>
                : "Any style"}
            </span>
          </button>
          {open==="style" && (
            <PortalDropdown triggerRef={styleRef} align="right" width={320} onMount={addPortalNode} onUnmount={removePortalNode}>
              <div style={{ fontSize:11, letterSpacing:1.5, textTransform:"uppercase", color:"#8a8a9a", fontWeight:600, marginBottom:12 }}>Yoga style</div>
              <div className="ys-style-grid">
                {STYLES.filter(s=>s!=="All").map(s=>(
                  <button key={s} className={`ys-style-chip${filterStyle===s?" selected":""}`}
                    onMouseDown={e=>{e.stopPropagation();setFilterStyle(s===filterStyle?"All":s);setOpen(null);}}>
                    <StyleDot style={s} size={9} />
                    <span>{s}</span>
                  </button>
                ))}
              </div>
              {filterStyle && filterStyle !== "All" && (
                <button onMouseDown={e=>{e.stopPropagation();setFilterStyle("All");setOpen(null);}}
                  style={{ marginTop:10, width:"100%", padding:"7px", background:"none", border:"none", cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, color:"#B5703F" }}>
                  Clear selection
                </button>
              )}
            </PortalDropdown>
          )}

          {/* FILTERS — only where a page passes extraFilters */}
          {extraFilters && (() => {
            const v = extraFilters.value || {};
            const setF = (k, val) => extraFilters.onChange({ ...v, [k]: val });
            const n = [v.priceMin, v.priceMax, v.language].filter(x => x !== "" && x != null).length + (v.accommodation ? 1 : 0) + (v.yaOnly ? 1 : 0);
            const sym = ({ USD:"$", INR:"₹", EUR:"€", GBP:"£", SGD:"S$", AED:"د.إ" })[extraFilters.currency] || "$";
            const field = { width:"100%", padding:"9px 11px", border:"1.5px solid rgba(181,112,63,0.3)", borderRadius:8, fontFamily:"'Jost',sans-serif", fontSize:13.5, color:"#2E2A24", background:"white", boxSizing:"border-box" };
            const label = { display:"block", fontSize:11, letterSpacing:1.5, textTransform:"uppercase", color:"#8a8a9a", fontWeight:600, margin:"0 0 6px" };
            const check = { display:"flex", alignItems:"center", gap:8, fontSize:13.5, color:"#2E2A24", fontFamily:"'Jost',sans-serif", cursor:"pointer", margin:"0 0 10px" };
            return (<>
              <button ref={filtersRef} className={`ys-pill${open==="filters"?" active":""}`}
                onClick={()=>setOpen(o=>o==="filters"?null:"filters")}>
                <span className="ys-pill-label">Filters</span>
                <span className={`ys-pill-value${n?" filled":" empty"}`}>{n ? `${n} selected` : "Price, stay & more"}</span>
              </button>
              {open==="filters" && (
                <PortalDropdown triggerRef={filtersRef} align="right" width={300} onMount={addPortalNode} onUnmount={removePortalNode}>
                  <span style={label}>Price per person ({extraFilters.currency || "USD"})</span>
                  <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:14 }}>
                    <input type="number" min="0" inputMode="numeric" placeholder={`Min ${sym}`} aria-label="Minimum price" value={v.priceMin ?? ""} onChange={e=>setF("priceMin", e.target.value)} style={field} />
                    <span style={{ color:"#8a8a9a" }}>–</span>
                    <input type="number" min="0" inputMode="numeric" placeholder={`Max ${sym}`} aria-label="Maximum price" value={v.priceMax ?? ""} onChange={e=>setF("priceMax", e.target.value)} style={field} />
                  </div>
                  {(extraFilters.languages || []).length > 1 && (<>
                    <span style={label}>Language of instruction</span>
                    <select value={v.language || ""} onChange={e=>setF("language", e.target.value)} style={{ ...field, marginBottom:14 }} aria-label="Language of instruction">
                      <option value="">Any language</option>
                      {extraFilters.languages.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </>)}
                  <label style={check}><input type="checkbox" checked={!!v.accommodation} onChange={e=>setF("accommodation", e.target.checked)} />Accommodation included</label>
                  <label style={check} title="The school lists a Yoga Alliance registry ID — check it on Yoga Alliance's directory.">
                    <input type="checkbox" checked={!!v.yaOnly} onChange={e=>setF("yaOnly", e.target.checked)} />Lists a Yoga Alliance registry ID
                  </label>
                  <div style={{ display:"flex", gap:8, marginTop:6 }}>
                    {n > 0 && <button type="button" onMouseDown={e=>{e.stopPropagation(); extraFilters.onChange({ ...v, priceMin:"", priceMax:"", language:"", accommodation:false, yaOnly:false });}}
                      style={{ flex:1, padding:"8px", background:"none", border:"1px solid rgba(181,112,63,0.3)", borderRadius:8, cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12.5, color:"#B5703F" }}>Clear</button>}
                    <button type="button" onMouseDown={e=>{e.stopPropagation(); setOpen(null);}}
                      style={{ flex:2, padding:"8px", background:"#2E2A24", border:"none", borderRadius:8, cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12.5, color:"white", fontWeight:600 }}>Done</button>
                  </div>
                </PortalDropdown>
              )}
            </>);
          })()}

          {/* SEARCH BUTTON */}
          <button className="ys-search-btn" onClick={()=>{
            setOpen(null);
            trackEvent({
              event_type:     "search",
              destination:    search||null,
              style:          filterStyle&&filterStyle!=="All"?filterStyle:null,
              selected_month: selectedMonth||null,
            });
            submitSearch();
          }}>
            <Search size={15} color="currentColor" />
            <span>Search</span>
          </button>
        </div>

        {/* ── Popular pills ── */}
        {showPopular && (
          <div className="ys-popular">
            <span style={{ fontSize:11, color:isHero?"rgba(255,255,255,0.5)":"#8a8a9a", alignSelf:"center", fontFamily:"'Jost',sans-serif" }}>Popular:</span>
            {POPULAR.map(p=>(
              <button key={p} className={`ys-popular-pill${isHero?"":" page"}`}
                onClick={()=>{setSearch(p);submitSearch(p);}}>
                {p}
              </button>
            ))}
          </div>
        )}

        {/* ── Quiz + Concierge CTAs ── */}
        {(showQuiz || showConcierge) && (
          <div style={{ display:"flex", gap:10, marginTop:14, flexWrap:"wrap", justifyContent:"center" }}>
            {showQuiz && (
              <button onClick={()=>openQuiz()}
                style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 18px", background:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.25)", borderRadius:20, cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, color:isHero?"white":"#4a4a5a", backdropFilter:"blur(4px)", transition:"all 0.15s", ...(isHero?{}:{ background:"rgba(181,112,63,0.08)", border:"1px solid rgba(181,112,63,0.25)" }) }}>
                <Sparkles size={14} />
                <span>Not sure? Take our 1-min quiz</span>
              </button>
            )}
            {showConcierge && (
              <button onClick={()=>setShowConciergeModal(true)}
                style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 18px", background:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.25)", borderRadius:20, cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, color:isHero?"white":"#4a4a5a", backdropFilter:"blur(4px)", transition:"all 0.15s", ...(isHero?{}:{ background:"rgba(181,112,63,0.08)", border:"1px solid rgba(181,112,63,0.25)" }) }}>
                <Flower2 size={14} />
                <span>Let our team hand-pick for you</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Modals (portaled) ── */}
      {showQuizModal && createPortal(
        <QuizModal onClose={()=>setShowQuizModal(false)} onComplete={handleQuizComplete} ytts={quizYtts} />,
        document.body
      )}
      {showConciergeModal && createPortal(
        <ConciergeModal
          onClose={()=>setShowConciergeModal(false)}
          quizAnswers={quizResult}
        />,
        document.body
      )}
    </>
  );
}

// ─── INTEREST COUNTER HOOK ────────────────────────────────────────────────────
// Add to each retreat card: const interested = useInterestCount(retreatId);
// Stores in localStorage, increments on page view, resets after 7 days
export function useInterestCount(retreatId, baseCount = 0) {
  const [count, setCount] = useState(baseCount);

  useEffect(() => {
    if (!retreatId) return;
    try {
      const key = `ys_interest_${retreatId}`;
      const stored = JSON.parse(localStorage.getItem(key) || "{}");
      const now = Date.now();
      // Reset if older than 7 days
      if (stored.ts && now - stored.ts > 7 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(key);
        return;
      }
      // Add this session's view
      if (!stored.viewed) {
        const next = { count: (stored.count||baseCount) + 1, ts: now, viewed: true };
        localStorage.setItem(key, JSON.stringify(next));
        setCount(next.count);
        // Track the view event
        trackEvent({ event_type: "interest_view", retreat_id: retreatId });
      } else {
        setCount(stored.count || baseCount);
      }
    } catch {}
  }, [retreatId, baseCount]);

  return count;
}

// ─── INTEREST BADGE COMPONENT ─────────────────────────────────────────────────
// Usage: <InterestBadge retreatId={r.id} base={r.interest_count||12} />
export function InterestBadge({ retreatId, base = 8 }) {
  const count = useInterestCount(retreatId, base);
  if (count < 3) return null;
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:5,
      fontSize:11, fontWeight:600, color:"#E07B2A",
      background:"rgba(224,123,42,0.1)", border:"1px solid rgba(224,123,42,0.2)",
      padding:"3px 9px", borderRadius:20, fontFamily:"'Jost',sans-serif",
      letterSpacing:0.3,
    }}>
      <Flame size={12} style={{ verticalAlign:"-1px", marginRight:5 }} />{count} {count === 1 ? "person" : "people"} interested
    </span>
  );
}
